// Branding / presentation config for this build.
//
// The single source of truth is branding.yaml in this directory: it carries
// per-platform (per-deployment) values for the login page look & content, the
// loading UI and the feature switches. Editing the YAML and rebuilding is all
// that is needed to produce a different deployment flavour.
//
// Which variant is active:
//   1. env var VITE_PLATFORM (set by a deploy script) — highest precedence;
//   2. activePlatform inside branding.yaml;
//   3. 'lab' — the safe fallback.
// Components MUST import from this module (or the YAML) instead of reading
// import.meta.env directly.
import rawConfig from './branding.yaml';
import labPng from '@/assets/lab.png';
import logoSvg from '@/assets/logo.svg';

// ---------------------------------------------------------------------------
// Assets
// ---------------------------------------------------------------------------
// Brand images live in src/assets/branding/ and are referenced by their file
// name (without extension) in the YAML. Dropping a new image into that folder
// makes it available without touching code.
const brandAssetModules = import.meta.glob('@/assets/branding/*.{png,svg,jpg,jpeg,gif,webp}', {
    eager: true,
    import: 'default',
});
const BRAND_ASSETS: Record<string, string> = {
    lab: labPng,
    logo: logoSvg,
};
for (const [globPath, url] of Object.entries(brandAssetModules)) {
    const fileName = globPath.split('/').pop() ?? '';
    const key = fileName.replace(/\.[^.]+$/, '');
    BRAND_ASSETS[key] = url as string;
}

const resolveBrandAsset = (key?: string): string => {
    if (!key) return '';
    return BRAND_ASSETS[key] ?? '';
};

// ---------------------------------------------------------------------------
// Raw YAML -> typed data (permissive: missing keys fall back to safe defaults)
// ---------------------------------------------------------------------------
type RawVariant = Record<string, any>;

interface LoginBranding {
    layout: 'lab' | 'hub';
    productLogo: string;
    productLogoClass: string;
    panelClass: string;
    mobilePanelClass: string;
    titleClass: string;
    taglineClass: string;
    companyLogoClass: string;
    companyLogo?: string;
    companySiteUrl?: string;
    titleI18nKey: string;
    taglineI18nKey?: string;
    showFooter: boolean;
    copyrightI18nKey?: string;
    icpBeian?: string;
    aboutUsUrlZh?: string;
    aboutUsUrlEn?: string;
    termsUrlZh: string;
    termsUrlEn: string;
    privacyUrlZh?: string;
    privacyUrlEn?: string;
    cookiesUrlZh?: string;
    cookiesUrlEn?: string;
    emailLabelI18nKey: string;
    emailPlaceholderI18nKey: string;
    termsPrefixI18nKey: string;
    footerNoteI18nKey: string;
    welcomeFontSize: string;
    showSignInSubtitle: boolean;
    showRegisterLinks: boolean;
}

interface LoadingBranding {
    messageI18nKey: string;
    bilingualInline: boolean;
}

const str = (v: any, fallback = ''): string => (typeof v === 'string' && v ? v : fallback);
const bool = (v: any): boolean => v === true;

const buildLoginBranding = (login: RawVariant): LoginBranding => ({
    layout: login?.layout === 'hub' ? 'hub' : 'lab',
    productLogo: resolveBrandAsset(login?.productLogo) || labPng,
    productLogoClass: str(login?.productLogoClass, 'h-40 w-40'),
    panelClass: str(
        login?.panelClass,
        'lg:w-3/5 order-2 lg:order-1 p-6 lg:p-8 flex flex-col justify-center items-center text-white bg-gradient-to-br from-gray-800 to-gray-900'
    ),
    mobilePanelClass: str(
        login?.mobilePanelClass,
        'lg:hidden flex flex-col items-center text-white bg-gradient-to-br from-gray-800 to-gray-900 p-8'
    ),
    titleClass: str(login?.titleClass, 'text-3xl md:text-4xl font-bold mb-6 text-blue-300 text-center'),
    taglineClass: str(login?.taglineClass, 'mb-8 text-lg italic text-blue-200 text-center'),
    companyLogoClass: str(login?.companyLogoClass, 'h-10'),
    companyLogo: resolveBrandAsset(login?.companyLogo) || undefined,
    companySiteUrl: str(login?.companySiteUrl),
    titleI18nKey: str(login?.titleI18nKey, 'login.title'),
    taglineI18nKey: str(login?.taglineI18nKey),
    showFooter: bool(login?.showFooter),
    copyrightI18nKey: str(login?.copyrightI18nKey),
    icpBeian: str(login?.icpBeian),
    aboutUsUrlZh: str(login?.aboutUsUrl?.zh),
    aboutUsUrlEn: str(login?.aboutUsUrl?.en),
    termsUrlZh: str(login?.termsUrl?.zh),
    termsUrlEn: str(login?.termsUrl?.en),
    privacyUrlZh: str(login?.privacyUrl?.zh),
    privacyUrlEn: str(login?.privacyUrl?.en),
    cookiesUrlZh: str(login?.cookiesUrl?.zh),
    cookiesUrlEn: str(login?.cookiesUrl?.en),
    emailLabelI18nKey: str(login?.emailLabelI18nKey, 'login.email_label'),
    emailPlaceholderI18nKey: str(login?.emailPlaceholderI18nKey, 'login.email_placeholder'),
    termsPrefixI18nKey: str(login?.termsPrefixI18nKey, 'register.terms_prefix'),
    footerNoteI18nKey: str(login?.footerNoteI18nKey),
    welcomeFontSize: str(login?.welcomeFontSize),
    showSignInSubtitle: login?.showSignInSubtitle !== false,
    showRegisterLinks: login?.showRegisterLinks !== false,
});

const buildLoadingBranding = (loading: RawVariant): LoadingBranding => ({
    messageI18nKey: str(loading?.messageI18nKey, 'user_ready.loading'),
    bilingualInline: bool(loading?.bilingualInline),
});

// ---------------------------------------------------------------------------
// Active variant resolution
// ---------------------------------------------------------------------------
export const PLATFORM_CONFIG: string = (import.meta.env.VITE_PLATFORM as string) || '';
const variants: Record<string, RawVariant> = rawConfig?.variants ?? {};
const activePlatform: string =
    PLATFORM_CONFIG || str(rawConfig?.activePlatform, 'lab') || 'lab';
const variant: RawVariant = variants[activePlatform] ?? variants['lab'] ?? {};

export const webTitle: string = str(variant?.webTitle, 'Aitomistic');

// 平台级固定语言（如 hiam 锁 zh）。为空时 i18n 保持浏览器自动检测。
export const fixedLocale: string = str(variant?.locale);

// Feature switches — read from grouping.features in the active variant.
export const enablePayment: boolean = bool(variant?.features?.payment);
export const enableCourseApply: boolean = bool(variant?.features?.courseApply);
export const enableApiAccess: boolean = bool(variant?.features?.apiAccess);

// 浏览器标签页图标（public/ 下的 URL 路径）
export const favicon: string = str(variant?.favicon, '/logo.png');

export const branding: {
    platform: string;
    webTitle: string;
    favicon: string;
    login: LoginBranding;
    loading: LoadingBranding;
} = {
    platform: activePlatform,
    webTitle,
    favicon,
    login: buildLoginBranding(variant?.login),
    loading: buildLoadingBranding(variant?.loading),
};